import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Navigate, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import { setState } from "../store/globalSlice"
import errors from "../utils/errors"
import { socket } from "../utils/utils"
import {
  attemptGoogleAutoSignIn,
  isGoogleLoginAvailable,
  onGoogleSignInSucceeded,
  signInWithGoogle,
} from "../utils/googleAuth"
import AuthLayout, {
  AuthPasswordToggle,
  AuthPhoneBackdrop,
  authButton,
  authGhostButton,
  authInlineLink,
  authInput,
  authMutedText,
  authPrimaryButton,
  authSubtleLink,
  authTallControl,
} from "../Components/AuthLayout"
import wallpaper from "@/images/backgrounds/wallpaper-login.webp"
import dealMasterLogo from "@/images/backgrounds/logo-without-dice.png"

// Inlined rather than an asset import: Google's terms require their own mark on
// the button, and four <path>s cost less than another file in the bundle.
const GoogleGlyph = () => (
  <svg
    className="h-5 w-5 mobile:h-10 mobile:w-10 shrink-0"
    viewBox="0 0 48 48"
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
)

interface LoginProps {}

const Login: React.FC<LoginProps> = () => {
  const isSteamBuild = import.meta.env.VITE_BUILD_TARGET === "steam"
  // The app store builds have no registration screen to send anyone to, so the
  // username/password form would be a dead end for everyone but the handful of
  // players who made an account on the web: Google or guest is the whole menu.
  // Keyed off isGoogleLoginAvailable rather than the build target on its own so
  // a mobile build missing its client id still keeps a way into an account.
  const isPasswordLoginHidden = isGoogleLoginAvailable
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [guestName, setGuestName] = useState("")
  const [isLoginInProgress, setIsLoginInProgress] = useState(false)
  const [isSteamLoginInProgress, setIsSteamLoginInProgress] = useState(false)
  const [isGoogleLoginInProgress, setIsGoogleLoginInProgress] = useState(false)
  const [steamLoginError, setSteamLoginError] = useState("")
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector((state) => state.global.isLoggedIn)
  const nav = useNavigate()

  const completeLogin = useCallback(
    (user: LoginRequestResponse) => {
      localStorage.setItem("user", JSON.stringify(user))
      // Whatever they signed in with, they are clearly not trying to stay signed
      // out any more, so the suppression a previous logout set can go.
      onGoogleSignInSucceeded()
      dispatch(setState({ isLoggedIn: true }))
      nav("/lobby")
    },
    [dispatch, nav]
  )

  const exchangeGoogleIdToken = useCallback(
    (idToken: string) =>
      socket.emit("google-login", { idToken }, (user) => {
        // A false here already came with an "error" event from the server, which
        // App.tsx surfaces, so there is nothing to add.
        if (user) completeLogin(user)
      }),
    [completeLogin]
  )

  const onGoogleLogin = useCallback(async () => {
    setIsGoogleLoginInProgress(true)
    try {
      const idToken = await signInWithGoogle()
      // null means they backed out of the sheet, which is not an error.
      if (idToken) exchangeGoogleIdToken(idToken)
    } catch (error) {
      console.warn("Google sign-in failed", error)
      Swal.fire({
        title: "Google sign-in failed",
        // The button only exists on the builds that dropped the password form,
        // so pointing at a username and password would point at nothing.
        text: "We couldn't sign you in with Google. Please try again, or play as a guest for now.",
        icon: "error",
        heightAuto: false,
      })
    } finally {
      setIsGoogleLoginInProgress(false)
    }
  }, [exchangeGoogleIdToken])

  const attemptSteamSilentLogin = useCallback(() => {
    if (!window.electronApi?.getSteamAuthSession) {
      setSteamLoginError(
        "Steam bridge is unavailable. Please launch from Steam."
      )
      setIsSteamLoginInProgress(false)
      return
    }

    setSteamLoginError("")
    setIsSteamLoginInProgress(true)
    window.electronApi
      .getSteamAuthSession()
      .then((steamSession) => {
        socket.emit(
          "steam-login",
          {
            steamId64: steamSession.steamId64,
            playerName: steamSession.playerName,
            ticket: steamSession.ticket,
          },
          (user) => {
            if (user) {
              completeLogin(user)
            } else {
              setSteamLoginError(
                "Steam authentication failed. Please restart Steam and try again."
              )
            }
          }
        )
      })
      .catch((error) => {
        console.warn("Steam silent login unavailable", error)
        setSteamLoginError(
          "Unable to connect to Steam. Please relaunch the game from Steam."
        )
      })
      .finally(() => {
        setIsSteamLoginInProgress(false)
      })
  }, [completeLogin])

  const onGuestLogin = () => {
    if (!guestName.trim()) {
      Swal.fire({
        title: "Enter a name",
        text: "Please enter a username to play as guest.",
        icon: "error",
        heightAuto: false,
      })
      return
    }
    socket.emit("guest-login", guestName.trim(), (user) => {
      if (user) {
        localStorage.setItem("user", JSON.stringify(user))
        dispatch(setState({ isLoggedIn: true }))
        nav("/lobby")
      }
    })
  }

  const onLogin = () => {
    if (!username || !password) {
      Swal.fire({
        ...errors.auth.allFieldRequired,
        icon: "error",
        heightAuto: false,
      })
      return
    }

    setIsLoginInProgress(true)

    // socket.io quietly drops a pending ack if the connection goes down, and
    // the button is disabled until that ack lands — without a fallback a
    // dropped connection strands it there until the player reloads. Whichever
    // of the two arrives first releases it.
    let settled = false
    let timer: ReturnType<typeof setTimeout>
    const release = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      setIsLoginInProgress(false)
    }
    timer = setTimeout(release, 15000)

    socket.emit("login", { username, password }, (user) => {
      // A falsy user arrived with its own "error" event, so the only thing left
      // to do here is hand the button back.
      release()
      if (user) {
        completeLogin(user)
      }
    })
  }
  useEffect(() => {
    if (!isSteamBuild) return
    if (isLoggedIn) return

    attemptSteamSilentLogin()
  }, [attemptSteamSilentLogin, isSteamBuild, isLoggedIn])

  // Reaching this screen at all means there was no stored session to restore,
  // so this is the first run / post-reinstall case where a returning player can
  // be signed back in without touching anything. Silent by design: it resolves
  // to null whenever Google cannot do it without asking.
  useEffect(() => {
    if (!isGoogleLoginAvailable) return
    if (isLoggedIn) return

    let cancelled = false
    attemptGoogleAutoSignIn().then((idToken) => {
      if (!cancelled && idToken) exchangeGoogleIdToken(idToken)
    })
    return () => {
      cancelled = true
    }
  }, [exchangeGoogleIdToken, isLoggedIn])
  if (isLoggedIn) {
    return <Navigate to="/lobby"></Navigate>
  }
  if (isSteamBuild) {
    // No form to show and nothing to choose, so this one skips the two-column
    // shell: the art the other screens keep for phones carries the whole
    // window, with the status card centred on it.
    return (
      <div className="relative flex h-full w-full flex-col items-center justify-center bg-brown-900 px-6">
        <div className="pointer-events-none absolute inset-0 mobile:hidden">
          <img
            src={wallpaper}
            className="h-full w-full object-cover contrast-110"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        </div>
        <AuthPhoneBackdrop />

        <div className="relative w-full max-w-[520px] rounded-3xl border border-white/20 bg-black/60 px-10 py-10 text-center shadow-[0_24px_70px_rgba(0,0,0,0.65)] backdrop-blur-xl">
          <img
            src={dealMasterLogo}
            className="mx-auto mb-8 w-full max-w-[260px]"
            alt="Deal Master"
          />
          <div className="mb-8 flex justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/25 border-t-red-400"></div>
          </div>
          <div className="mb-3 font-third text-xl text-white mobile:text-4xl">
            {isSteamLoginInProgress
              ? "Signing you in through Steam..."
              : "Steam login did not complete."}
          </div>
          {steamLoginError ? (
            <div className="mb-8 font-third text-base text-amber-200 mobile:text-3xl">
              {steamLoginError}
            </div>
          ) : null}
          {!isSteamLoginInProgress ? (
            <button
              onClick={attemptSteamSilentLogin}
              className={`${authPrimaryButton} mt-2`}
            >
              Retry Steam Login
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <AuthLayout
      wide
      brandAside={isPasswordLoginHidden}
      wallpaper={wallpaper}
      marketingTitle="Rule the table online"
      marketingBody={
        <>
          <div>Challenge friends in the ultimate Deal Master showdown.</div>
          <div>Build your empire, make smart trades, and chase every win.</div>
        </>
      }
      marketingCta={
        isPasswordLoginHidden
          ? undefined
          : {
              to: "/register",
              label: "Create Account",
              testId: "go-to-register",
            }
      }
      heading="Log in"
      subheading="Welcome back — your empire is waiting."
      footer={
        <>
          {/* The marketing half carries the Create Account button on desktop,
              but it is hidden at phone widths, so registration needs a way in
              here. */}
          {isPasswordLoginHidden ? null : (
            <div
              className={`mt-8 hidden text-center sm:mt-4 mobile:mt-[16px] mobile:block sm:mobile:!mt-[8px] ${authMutedText}`}
            >
              New to Deal Master?{" "}
              <Link to="/register" className={authInlineLink}>
                Create an account
              </Link>
            </div>
          )}

          <div
            className={`mt-6 text-center text-sm mobile:mt-[12px] mobile:text-2xl sm:mt-3 sm:mobile:!mt-[14px] sm:mobile:!text-[11px]/[1.3] ${authMutedText}`}
          >
            By continuing you agree to Deal Master's{" "}
            <Link
              to="/privacy-policy"
              data-testid="privacy-policy-link"
              className={authInlineLink}
            >
              Privacy Policy
            </Link>
            .
          </div>
        </>
      }
    >
      {isPasswordLoginHidden ? (
        /* The app store build's entire menu. It only ever renders on a phone,
           and the game holds that phone in landscape, so this is laid out for
           the one shape: AuthLayout stands the logo up as the column to its
           left, which leaves this a short stack of controls with the room to
           space them properly. No bordered box around the guest half either —
           inside a card that is already a box it read as clutter, and the rule
           below carries the split on its own. */
        <div className="flex flex-col gap-4 mobile:gap-[14px] sm:mobile:!gap-[20px]">
          <button
            onClick={onGoogleLogin}
            disabled={isGoogleLoginInProgress}
            // Google's branding guidelines: their own white surface, #dadce0
            // hairline and near-black label. It must not inherit the app's red
            // pill, and it must not be brightness-filtered on hover the way
            // `.btn:hover` does it, or the four-colour mark washes out. The one
            // liberty taken is the outer glow, which sits off the surface and
            // is what makes it read as the way in rather than a form control.
            className={`${authButton} ${authTallControl} border border-[#dadce0] bg-white !font-medium text-[#1f1f1f] shadow-[0_1px_3px_rgba(60,64,67,0.25)] hover:bg-[#f7f8f8] hover:shadow-[0_2px_6px_rgba(60,64,67,0.3)] mobile:shadow-[0_10px_30px_rgba(0,0,0,0.45),0_0_26px_rgba(255,255,255,0.28)]`}
            data-testid="google-login-btn"
          >
            <GoogleGlyph />
            {isGoogleLoginInProgress ? "Signing in..." : "Continue with Google"}
          </button>

          <div className="flex items-center gap-4 mobile:gap-[12px]">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-200/40"></div>
            <span className="rounded-full border border-amber-200/25 bg-white/5 px-[14px] py-[4px] font-third tracking-widest text-primary/60 mobile:text-2xl sm:mobile:!text-[11px]/[1.2]">
              OR
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-200/40"></div>
          </div>

          <div className="flex flex-col gap-3 mobile:gap-[12px] sm:mobile:!gap-[14px]">
            <input
              className={`${authInput} sm:mobile:!min-h-[50px] sm:mobile:!text-[17px]/[1.2]`}
              data-testid="guest-name"
              value={guestName}
              placeholder="Choose a display name"
              onChange={(e) => setGuestName(e.target.value)}
            ></input>
            <button
              onClick={onGuestLogin}
              className={`${authGhostButton} ${authTallControl}`}
              data-testid="guest-login-btn"
            >
              Play as Guest
            </button>
          </div>
        </div>
      ) : (
        /* The web build, which still has a password form to place. The game
           pushes players into landscape, so a short screen is the normal phone
           case here too, not an edge case: stacked, the two halves overflow a
           390px-tall viewport; side by side they fit without scrolling and
           finally use the width landscape hands us. */
        <div className="sm:grid sm:grid-cols-2 sm:items-start sm:gap-8 sm:mobile:!gap-[28px]">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (!isLoginInProgress) onLogin()
            }}
            className="flex flex-col"
          >
            {/* The spacing below is in raw px for the same reason authCard's
                padding is: at phone width index.css puts the root font at 8px,
                so `gap-3` was laying out 6px of gap rather than the 12 it
                reads as, and the whole form came out cramped. */}
            <div className="mb-3 flex flex-col gap-3 sm:gap-2 mobile:mb-[16px] mobile:gap-[12px] sm:mobile:!mb-[14px]">
              <input
                className={authInput}
                data-testid="username"
                name="username"
                autoComplete="username"
                value={username}
                placeholder="Username or Email"
                onChange={(e) => setUsername(e.target.value)}
              ></input>
              <div className="relative">
                <input
                  className={`${authInput} !pr-16 mobile:!pr-24`}
                  data-testid="password"
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  placeholder="Password"
                  onChange={(e) => setPassword(e.target.value)}
                ></input>
                <AuthPasswordToggle
                  open={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              </div>
            </div>

            <Link
              to="/forgot-pass"
              data-testid="reset-pass-btn"
              className={`mb-6 self-end sm:mb-3 mobile:mb-[16px] sm:mobile:!mb-[12px] ${authSubtleLink}`}
            >
              Forgot password?
            </Link>

            <button
              type="submit"
              disabled={isLoginInProgress}
              className={authPrimaryButton}
              data-testid="login-btn"
            >
              {isLoginInProgress ? "Logging in..." : "Login"}
            </button>
          </form>

          <div className="sm:border-l sm:border-brown-300 sm:pl-8 sm:mobile:!pl-[28px] mobile:border-amber-200/20">
            {/* Side by side the horizontal rule reads as a divider between the
                two halves of the form rather than a step after it, so the
                border on the column takes over that job on short screens. */}
            <div className="my-6 flex items-center gap-4 mobile:my-[16px] mobile:gap-[12px] sm:hidden">
              <div className="h-px flex-1 bg-brown-300 mobile:bg-transparent mobile:bg-gradient-to-r mobile:from-transparent mobile:to-amber-200/40"></div>
              <span className="font-third text-sm tracking-widest text-gray-500 mobile:rounded-full mobile:border mobile:border-amber-200/25 mobile:bg-white/5 mobile:px-[14px] mobile:py-[5px] mobile:text-2xl mobile:text-primary/60">
                OR
              </span>
              <div className="h-px flex-1 bg-brown-300 mobile:bg-transparent mobile:bg-gradient-to-l mobile:from-transparent mobile:to-amber-200/40"></div>
            </div>

            {/* No top margin: the Google button that used to sit above this
                only exists on the builds that took the password form away, and
                on a short screen this box has to line up with the top of the
                form in the column beside it. */}
            <div className="rounded-3xl border border-brown-300 bg-brown-200/60 px-6 py-5 sm:py-3 mobile:border-amber-200/25 mobile:bg-transparent mobile:bg-gradient-to-b mobile:from-amber-400/10 mobile:to-black/20 mobile:px-[18px] mobile:py-[15px] mobile:shadow-[inset_0_1px_0_rgba(251,191,36,0.25)] sm:mobile:!px-[14px] sm:mobile:!py-[16px]">
              <div
                // `text-wrap: balance` rather than a smaller size: at 314px of
                // portrait card this line breaks one word short and leaves
                // "game." orphaned. Older WebKit just ignores it.
                className={`mb-3 text-center sm:mb-2 mobile:mb-[10px] mobile:[text-wrap:balance] sm:mobile:!text-[13px]/[1.35] ${authMutedText}`}
              >
                In a hurry? Jump straight into a game.
              </div>
              <input
                className={`${authInput} mb-3 sm:mb-2 mobile:!mb-[12px]`}
                data-testid="guest-name"
                value={guestName}
                placeholder="Choose a display name"
                onChange={(e) => setGuestName(e.target.value)}
              ></input>
              <button
                onClick={onGuestLogin}
                className={authGhostButton}
                data-testid="guest-login-btn"
              >
                Play as Guest
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  )
}

export default Login
